/**
 * Module-level activity pub/sub for the BFF session-refresh timer (see useSessionTimer.ts).
 * Deliberately has no React dependency so any call site — a component handler, a plain
 * API-wrapper function, code that hasn't been converted to a hook — can import and call
 * notifySessionActivity() directly without needing to be inside the component tree that
 * mounts the timer.
 */

type ActivityListener = () => Promise<void> | void;

const activityListeners = new Set<ActivityListener>();

/**
 * Rejection reason from an awaited {@link notifySessionActivity} when the refresh it drove came
 * back 401: the session is confirmed gone and the redirect-to-login flow has already started.
 * A caller awaiting the ping must abandon whatever it was about to send rather than firing a
 * request that can only 401 in turn.
 */
export class SessionEndedError extends Error {
  constructor() {
    super('The BFF session ended while refreshing it.');
    this.name = 'SessionEndedError';
  }
}

/**
 * Ensure the BFF's stored IdP access token is current and WAIT for it, so the caller's next
 * request goes out behind a good token rather than one that has been ageing for the rest of the
 * refresh interval. For call sites that are about to spend the token — see `api/applications.ts`.
 *
 * The BFF spends an actual refresh grant only if that token has aged out (see
 * `AuthEndpoints.HandleRefresh`); on a token with minutes of life left this settles having done
 * nothing but slide the session cookie, which is exactly what the caller needed to hear.
 *
 * <h3>The ordering is the whole point</h3>
 * The returned promise settles when the refresh does. Fired and forgotten, the refresh and the
 * call it was meant to protect merely race, and the call can still leave on the old token.
 *
 * <h3>Once per action</h3>
 * Two of these around one action are two concurrent `POST /auth/refresh` grants. ISAM rotates
 * the refresh token on use, so the loser sends one the winner has already consumed, gets
 * `invalid_grant`, and the BFF ends the session — logging the advisor out mid-action. Awaiting
 * serializes whatever pings remain; it does not make a duplicate ping safe.
 *
 * <h3>Two ways this rejects, and they are not the same news</h3>
 * {@link SessionEndedError} — the refresh confirmed the session is gone and the redirect to
 * login has already started. {@link SessionRefreshUnavailableError} — the caller's token was
 * already spent and the refresh that should have replaced it did not land, but the session is
 * not known to be gone; nothing is redirecting, and the caller should surface an ordinary
 * retryable failure. Either way the caller must abandon its request.
 *
 * A transient refresh failure on a token that was still VALID resolves normally: a blip at the
 * IdP must not stop an advisor saving with a token that is perfectly good. That is the whole
 * distinction the second error above draws — see useSessionTimer's activity subscription.
 */
export function ensureSessionRefreshed(): Promise<void> {
  // A listener that throws synchronously must still surface as a rejection, not as a throw
  // out of this function — callers await it and would otherwise have to guard both.
  const settled = Array.from(activityListeners, (listener) => {
    try {
      return Promise.resolve(listener());
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
  return Promise.all(settled).then(() => undefined);
}

/**
 * Fire-and-forget version of {@link ensureSessionRefreshed}, for activity that keeps the session
 * warm without being about to spend the token itself — uploads, the submit hook, the delivery
 * poll. Call after (or as) a file upload, save, or submit action so the timer resets its cadence
 * rather than waiting out the rest of the current interval. See docs/pretend-advisor.md and
 * ADR-0010 for the call sites this is meant to guard.
 *
 * Returns `void` and never rejects, deliberately: these callers have nothing to do with a failure
 * — on a confirmed 401 the timer has already started the redirect — and a floating rejection at
 * six call sites is just noise in the console.
 */
export function notifySessionActivity(): void {
  void ensureSessionRefreshed().catch(() => undefined);
}

/**
 * Rejection reason from an awaited {@link ensureSessionRefreshed} when the caller's token was
 * already spent and the refresh meant to replace it did not succeed.
 *
 * Distinct from {@link SessionEndedError}: the session is NOT known to be gone — a blip at the
 * IdP looks exactly like this — so the redirect-to-login flow has not run and must not. What the
 * caller has to avoid is sending a request on a token it knows is dead, which BMO answers with a
 * 401 that reaches the advisor as an unexplained save failure. Surfacing it here instead makes
 * it an ordinary, visible, retryable error.
 *
 * A refresh that merely failed while the token was still good resolves normally, as before:
 * blocking every save on a transient IdP hiccup would be the worse failure.
 */
export class SessionRefreshUnavailableError extends Error {
  constructor() {
    super('The advisor session could not be refreshed before the request.');
    this.name = 'SessionRefreshUnavailableError';
  }
}

/** Internal — subscribed by useSessionTimer's effect. Returns an unsubscribe function. */
export function subscribeToSessionActivity(listener: ActivityListener): () => void {
  activityListeners.add(listener);
  return () => activityListeners.delete(listener);
}

/**
 * Cross-tab announcement that the BFF session is gone.
 *
 * The session is a single cookie shared by every tab, so when one tab's refresh comes back
 * 401 the others are dead too — they just don't know it yet, and would sit on a signed-out
 * app until their own tick happened to notice. Worse, once leader election means only one
 * tab is running the refresh interval (see useSessionTimer), the followers might have no
 * tick left to notice with. Telling them directly is what keeps the tabs consistent.
 */
const SESSION_CHANNEL_NAME = 'bmo-inflow.session';
const SESSION_ENDED_MESSAGE = 'session-ended';

/** Absent in jsdom and in older browsers; every use degrades to per-tab behaviour. */
function sessionChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(SESSION_CHANNEL_NAME);
  } catch {
    return null;
  }
}

export function broadcastSessionEnded(): void {
  const channel = sessionChannel();
  if (!channel) return;
  try {
    channel.postMessage(SESSION_ENDED_MESSAGE);
  } finally {
    channel.close();
  }
}

/** Returns an unsubscribe function. Never fires in the tab that broadcast. */
export function subscribeToSessionEnded(listener: () => void): () => void {
  const channel = sessionChannel();
  if (!channel) return () => undefined;
  const onMessage = (event: MessageEvent) => {
    if (event.data === SESSION_ENDED_MESSAGE) listener();
  };
  channel.addEventListener('message', onMessage);
  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
}
